import React, { ChangeEvent, ChangeEventHandler, Component } from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { ColorChangeHandler, SketchPicker } from 'react-color';

import { TextAnnotation } from '@paragon/contract-library';

import { ENABLED_FONTS } from '../../../modules/annotations/paperItemTranslator';
import AnnotationOptionsSection, { AnnotationsOptionsRow } from './AnnotationOptionsSection';
import FloatInput from './FloatInput';

const formSx = { display: 'flex', flex: 1, my: 2, pr: 1 } as const;

export const DEFAULT_FONT = 'IBM Plex Sans';
const DEFAULT_FONT_SIZE = 7.2;

interface Props {
  readonly defaultValue: string | undefined;
  readonly annotationBeingEdited: TextAnnotation | undefined;
  readonly onSizeChange: (scalingPercentage: number) => void;
}

interface State {
  readonly contents: string;
  readonly color: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly isBold: boolean;
  readonly isItalic: boolean;
  readonly isUnderlined: boolean;
  readonly displayAdvancedColorPicker: boolean;
}

export default class TextOptions extends Component<Props, State> {
  advancedColorButtonRef: React.RefObject<HTMLButtonElement | null> = React.createRef();

  state: State = {
    contents: this.props.annotationBeingEdited ? this.props.annotationBeingEdited.contents : '',
    color: this.props.annotationBeingEdited ? this.props.annotationBeingEdited.color : '#000',

    fontFamily:
      this.props.annotationBeingEdited &&
      this.props.annotationBeingEdited.fontFamily &&
      {}.hasOwnProperty.call(ENABLED_FONTS, this.props.annotationBeingEdited.fontFamily)
        ? this.props.annotationBeingEdited.fontFamily
        : DEFAULT_FONT,
    fontSize:
      this.props.annotationBeingEdited && this.props.annotationBeingEdited.fontSize
        ? this.props.annotationBeingEdited.fontSize
        : DEFAULT_FONT_SIZE,

    isBold:
      this.props.annotationBeingEdited && this.props.annotationBeingEdited.isBold
        ? this.props.annotationBeingEdited.isBold
        : false,
    isItalic:
      this.props.annotationBeingEdited && this.props.annotationBeingEdited.isItalic
        ? this.props.annotationBeingEdited.isItalic
        : false,
    isUnderlined:
      this.props.annotationBeingEdited && this.props.annotationBeingEdited.isUnderlined
        ? this.props.annotationBeingEdited.isUnderlined
        : false,

    displayAdvancedColorPicker: false,
  };

  onToggleAdvancedColorPickerVisibility = () => {
    this.setState((prevState) => ({
      displayAdvancedColorPicker: !prevState.displayAdvancedColorPicker,
    }));
  };

  onColorChange: ColorChangeHandler = (color) => {
    this.setState({
      color: color.hex,
    });
  };

  onContentsChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    const contents = event.target.value;

    this.setState({ contents });
  };

  onFontChange = (event: SelectChangeEvent<string>) => {
    const fontFamily = event.target.value;

    if (!{}.hasOwnProperty.call(ENABLED_FONTS, fontFamily)) {
      throw new Error(`Invalid font selected: ${fontFamily}`);
    }

    this.setState({ fontFamily });
  };

  onFontSizeChange = (newFontSize: number, oldFontSize: number) => {
    this.setState({ fontSize: newFontSize });

    const scalingPercentage = newFontSize / oldFontSize;
    this.props.onSizeChange(scalingPercentage);
  };

  onToggleBold = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ isBold: event.target.checked });
  };

  onToggleItalics = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ isItalic: event.target.checked });
  };

  onToggleUnderline = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ isUnderlined: event.target.checked });
  };

  get prohibitEditing() {
    return this.state.contents.trim() === '';
  }

  get updatedProperties() {
    return {
      fontSize: this.state.fontSize,
      fontFamily: this.state.fontFamily,
      color: this.state.color,
      contents: this.state.contents,
      isBold: this.state.isBold,
      isItalic: this.state.isItalic,
      isUnderlined: this.state.isUnderlined,
    };
  }

  adjustFontSize = (scalingPercentage: number) => {
    this.setState((prevState) => ({
      fontSize: prevState.fontSize * scalingPercentage,
    }));
  };

  render() {
    const fontOptions = Object.entries(ENABLED_FONTS).map(([name, fonts]) => (
      <MenuItem style={{ fontFamily: fonts }} key={name} value={name}>
        {name}
      </MenuItem>
    ));

    return (
      <AnnotationOptionsSection title="text" titleMargin={22} id="textEditingTools">
        <AnnotationsOptionsRow>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton>
              <Checkbox
                checked={this.state.isBold}
                onChange={this.onToggleBold}
                icon={<FormatBoldIcon />}
                checkedIcon={<FormatBoldIcon color="primary" />}
              />
            </IconButton>

            <IconButton>
              <Checkbox
                checked={this.state.isItalic}
                onChange={this.onToggleItalics}
                icon={<FormatItalicIcon />}
                checkedIcon={<FormatItalicIcon color="primary" />}
              />
            </IconButton>

            <IconButton>
              <Checkbox
                checked={this.state.isUnderlined}
                onChange={this.onToggleUnderline}
                icon={<FormatUnderlinedIcon />}
                checkedIcon={<FormatUnderlinedIcon color="primary" />}
              />
            </IconButton>

            <IconButton
              id="advancedColorOptionsButton"
              ref={this.advancedColorButtonRef}
              onClick={this.onToggleAdvancedColorPickerVisibility}
            >
              <FormatColorTextIcon sx={{ color: this.state.color }} />
            </IconButton>

            <Popover
              anchorEl={this.advancedColorButtonRef.current}
              open={this.state.displayAdvancedColorPicker}
              onClose={this.onToggleAdvancedColorPickerVisibility}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <SketchPicker color={this.state.color} onChangeComplete={this.onColorChange} disableAlpha />
            </Popover>

            <Box sx={{ display: 'flex', ml: 2 }}>
              <FormControl sx={formSx}>
                <FloatInput
                  label="Font size"
                  id="fontSizeInput"
                  helperText="pts."
                  value={this.state.fontSize}
                  onChange={this.onFontSizeChange}
                />
              </FormControl>

              <FormControl sx={formSx}>
                <InputLabel htmlFor="font-family">Font Family</InputLabel>
                <Select
                  value={this.state.fontFamily}
                  onChange={this.onFontChange}
                  inputProps={{ id: 'font-family' }}
                  label="Font Family"
                >
                  {fontOptions}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </AnnotationsOptionsRow>

        <AnnotationsOptionsRow>
          <TextField
            variant="filled"
            multiline
            fullWidth
            margin="normal"
            autoFocus
            label="Type Your Content Here"
            id="textInputArea"
            onChange={this.onContentsChange}
            defaultValue={this.props.defaultValue}
          />
        </AnnotationsOptionsRow>
      </AnnotationOptionsSection>
    );
  }
}
